/**
 * Consent Management for GDPR Compliance
 * Default: Analytics OFF (consent-first mode)
 * Explicit opt-in required via consent banner
 */

export interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  crash: boolean;
  bannerDismissed: boolean;
  timestamp: string;
}

const STORAGE_KEY = 'app_consent';

class ConsentManager {
  private state: ConsentState;

  constructor() {
    this.state = this.loadFromStorage();
  }

  private loadFromStorage(): ConsentState {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading consent:', error);
    }

    // Default: analytics OFF (consent-first mode), banner not dismissed
    return {
      analytics: false,
      marketing: false,
      crash: false,
      bannerDismissed: false,
      timestamp: new Date().toISOString(),
    };
  }

  private saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  }

  getConsent(): ConsentState {
    return { ...this.state };
  }

  setConsent(partial: Partial<Omit<ConsentState, 'timestamp'>>) {
    const oldState = { ...this.state };
    this.state = {
      ...this.state,
      ...partial,
      timestamp: new Date().toISOString(),
    };
    this.saveToStorage();

    console.log('[ConsentManager] Consent updated', {
      old: oldState,
      new: this.state,
      changed: partial,
    });

    // Emit event for listeners
    window.dispatchEvent(
      new CustomEvent('consent:changed', { detail: this.state })
    );
  }

  dismissBanner() {
    this.setConsent({ bannerDismissed: true });
  }

  resetBanner() {
    console.log('[ConsentManager] Resetting banner (bannerDismissed = false)');
    this.state.bannerDismissed = false;
    this.saveToStorage();
    window.dispatchEvent(
      new CustomEvent('consent:changed', { detail: this.state })
    );
    console.log('[ConsentManager] Banner reset complete, event fired');
  }

  hasConsented(key: keyof Omit<ConsentState, 'timestamp'>): boolean {
    return this.state[key] === true;
  }

  revokeAll() {
    console.log('[ConsentManager] Revoking all consent');
    this.setConsent({
      analytics: false,
      marketing: false,
      crash: false,
    });
  }
}

export const consentManager = new ConsentManager();

// React hook for components
import { useState, useEffect } from 'react';

export function useConsent() {
  const [consent, setConsent] = useState<ConsentState>(consentManager.getConsent());

  useEffect(() => {
    const handleChange = (e: Event) => {
      const customEvent = e as CustomEvent<ConsentState>;
      setConsent(customEvent.detail);
    };

    window.addEventListener('consent:changed', handleChange);
    return () => window.removeEventListener('consent:changed', handleChange);
  }, []);

  return {
    consent,
    setConsent: (partial: Partial<Omit<ConsentState, 'timestamp'>>) =>
      consentManager.setConsent(partial),
    hasConsented: (key: keyof Omit<ConsentState, 'timestamp' | 'bannerDismissed'>) =>
      consentManager.hasConsented(key),
    revokeAll: () => consentManager.revokeAll(),
    dismissBanner: () => consentManager.dismissBanner(),
    resetBanner: () => consentManager.resetBanner(),
  };
}
